import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  UserCheck,
  X,
  Search,
  Plus,
  Loader2,
  Mail,
  MailX,
  Trash2,
  Star,
  PenLine,
  Pencil,
} from 'lucide-react';
import { isConsultantSlot, isSiteContact } from '../consultantSlot';
import { primaryBadge } from '../primaryBadge';
import { canConsult } from '../../organizations/consultantOptions';

import type { SiteContact } from '../../../types/api';
import { sitesService } from '../../../services/sitesService';
import { contactsService } from '../../../services/contactsService';
import { usersService } from '../../../services/usersService';
import type { ContactResponse } from '../../../types/api';
import { contactRefusalMessage } from '../contactRefusal';
import { formatPhone, formatPhoneInput } from '../../../utils/formatPhone';
import { useToast } from '@/hooks/useToast';
import { Switch } from '../../../components/ui/switch';
import { useConfirm } from '../../../components/ui/confirm-dialog';

type ContactModalState = {
  type: 'consultant' | 'inspector' | 'site_contact';
  contact?: {
    id?: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    company?: string;
  };
};

type ContactEditModalProps = {
  editContactModal: ContactModalState;
  onClose: () => void;
  siteId: string;
  siteContacts: ContactResponse[] | undefined;
};

// "Primary" is held per slot: the consultant of record (stored as contractor
// or consultant) and the on-site contact (manager) are separate primaries.
function primaryGroup(role: string | undefined): string {
  return role === 'contractor' || role === 'consultant' ? 'consultant' : 'site';
}

export function ContactEditModal({
  editContactModal,
  onClose,
  siteId,
  siteContacts,
  mailsEveryone = false,
  organizationId,
  initialMode,
}: ContactEditModalProps & {
  mailsEveryone?: boolean;
  /** The site's client: its people on other sites are the site-contact pick list. */
  organizationId?: string;
  /** Where the tool opens: the pick list (default for site contacts) or the current list. */
  initialMode?: 'pick' | 'view';
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // --- Local state (only used by this modal) ---
  const [contactMode, setContactMode] = useState<
    | 'view'
    | 'select'
    | 'create'
    | 'edit'
    | 'pick'
    | 'search_existing'
    | 'search_users'
  >('view');
  const [pickQuery, setPickQuery] = useState('');
  // Edit a person's name, email, phone, company or title in place (Jim
  // 10/21). Staff rows are edited in People Hub; the API refuses them here.
  const [editForm, setEditForm] = useState<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    company: string;
    title: string;
  } | null>(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [newContactForm, setNewContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    role: 'owner' as
      | 'owner'
      | 'manager'
      | 'contractor'
      | 'inspector'
      | 'consultant',
    is_primary: true,
    notify_email: true,
  });
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState<
    (ContactResponse & { site_id: string })[]
  >([]);

  // The Consultant slot opens on the consultant list (Chris 10/22). Site
  // Contacts open on the current list with two actions, Switch On-site
  // Contact and Select Existing Contact — Jim 10/23 asked for the earlier
  // tool back ("there should only be two"); the client's people and the
  // People Hub both sit behind Select Existing Contact.
  // Keyed on what the modal was opened for, never on the prop object's
  // identity: the Lite page passes a fresh `{ type }` literal on each render,
  // and keying on it reset the mode and wiped the typed filter on every
  // parent refetch (Robert and Omar 10/29-10/30: "kicks me out of the search";
  // Jim at the walkthrough: "taking me out of it").
  const openedFor = editContactModal?.type;
  const openedContactId = editContactModal?.contact?.id;
  useEffect(() => {
    setContactMode(
      initialMode ?? (openedFor === 'consultant' ? 'search_users' : 'view')
    );
    setPickQuery('');
  }, [openedFor, openedContactId, initialMode]);

  const { data: clientPeople = [], isLoading: clientPeopleLoading } = useQuery({
    queryKey: ['client-contacts', organizationId],
    queryFn: () => contactsService.list({ organizationId, limit: 200 }),
    enabled: contactMode === 'pick' && !!organizationId,
    staleTime: 60 * 1000,
  });
  const onSiteEmails = new Set(
    (siteContacts || []).map((c) => (c.email || '').toLowerCase())
  );
  const pickList = clientPeople.filter((p) => {
    if (onSiteEmails.has((p.email || '').toLowerCase())) return false;
    const q = pickQuery.trim().toLowerCase();
    return (
      !q ||
      `${p.first_name ?? ''} ${p.last_name ?? ''} ${p.email ?? ''} ${p.company ?? ''}`
        .toLowerCase()
        .includes(q)
    );
  });

  // --- Queries ---

  // Search EnviroLink users for adding as site contacts
  const { data: userSearchData } = useQuery({
    queryKey: ['users-for-contact', userSearchQuery],
    queryFn: () =>
      usersService.getUsers({
        isActive: true,
        size: 20,
        search: userSearchQuery,
      }),
    enabled: contactMode === 'search_users' && userSearchQuery.length >= 2,
  });
  const userSearchResults =
    userSearchQuery.length >= 2 ? userSearchData?.items || [] : [];
  // The pick list's search reaches the whole people directory (everyone on
  // file on any site), not the People Hub user list: inspectors cannot read
  // users, so for them that search found nobody (Robert Reed 10/27, a Town
  // of Prosper contact already on four sites).
  const { data: peopleSearchData } = useQuery({
    queryKey: ['people-for-contact', userSearchQuery],
    queryFn: () => contactsService.list({ search: userSearchQuery, limit: 20 }),
    enabled: contactMode === 'pick' && userSearchQuery.length >= 2,
  });
  const peopleSearchResults =
    userSearchQuery.length >= 2
      ? (peopleSearchData || []).map((p) => ({
          id: p.id,
          firstName: p.first_name || '',
          lastName: p.last_name || '',
          email: p.email || '',
        }))
      : [];
  // People on file matching the same search, less anyone already on the
  // site or already in the client list (Jim 10/18 wanted Hub people addable
  // as plain contacts; 10/23 he saw no difference between the two lists).
  const pickEmails = new Set(
    pickList.map((p) => (p.email || '').toLowerCase())
  );
  const hubMatches =
    pickQuery.trim().length >= 2
      ? peopleSearchResults.filter(
          (u) =>
            !onSiteEmails.has((u.email || '').toLowerCase()) &&
            !pickEmails.has((u.email || '').toLowerCase())
        )
      : [];
  // Jim 10/28: a person who is already on the site used to vanish from the
  // search, so "no results" and then "already exists" on New contact.
  const alreadyHere =
    pickQuery.trim().length >= 2
      ? peopleSearchResults.filter((u) =>
          onSiteEmails.has((u.email || '').toLowerCase())
        )
      : [];
  // The Consultant slot lists the consultants straight away — no typing needed —
  // and never a client (Jim 2026-10-13); search narrows the same list.
  const consultantSlot = editContactModal.type === 'consultant';
  const { data: consultantPool } = useQuery({
    queryKey: ['users-consultant-pool'],
    queryFn: async () => {
      const [consultants, seniors, inspectors] = await Promise.all([
        usersService.getUsers({
          isActive: true,
          size: 100,
          role: 'consultant',
          includeLegacy: true,
        }),
        usersService.getUsers({
          isActive: true,
          size: 100,
          role: 'senior_consultant',
          includeLegacy: true,
        }),
        usersService.getUsers({
          isActive: true,
          size: 500,
          role: 'inspector',
          includeLegacy: true,
        }),
      ]);
      const seen = new Set<string>();
      const merged: any[] = [];
      for (const u of [
        ...consultants.items,
        ...seniors.items,
        ...inspectors.items,
      ]) {
        if (!seen.has(u.id)) {
          seen.add(u.id);
          merged.push(u);
        }
      }
      return merged;
    },
    enabled: consultantSlot && contactMode === 'search_users',
    staleTime: 5 * 60 * 1000,
  });
  // already in the Consultant slot — not offered again (Jarod was added twice, 10/15)
  const inConsultantSlot = new Set(
    (siteContacts || [])
      .filter((c) => c.is_active && isConsultantSlot(c))
      .map((c) => (c.email || '').toLowerCase())
  );
  // Jim 10/28: the current consultant stays in the list, marked, instead of
  // vanishing — typing "J" on Brookside 2 showed only Jeremy because Jenna,
  // Jennifer and Jarod were all hidden as "already in the slot".
  const consultantMatches = (consultantPool ?? [])
    .filter((u: any) => {
      if (!canConsult(u.role, u.secondaryRoles)) return false;
      const q = userSearchQuery.trim().toLowerCase();
      if (!q) return true;
      return `${u.firstName} ${u.lastName} ${u.email}`
        .toLowerCase()
        .includes(q);
    })
    .map((u: any) => ({
      ...u,
      currentConsultant: inConsultantSlot.has(
        String(u.email || '').toLowerCase()
      ),
    }));
  const staffSearchResults = consultantSlot
    ? consultantMatches
    : userSearchResults;

  // Debounced contact search
  useEffect(() => {
    if (contactSearchQuery.length < 2) {
      setContactSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const results = await sitesService.searchContacts(contactSearchQuery);
        const existingEmails = new Set(
          (siteContacts || []).map((c) => c.email.toLowerCase())
        );
        setContactSearchResults(
          results.filter((r) => !existingEmails.has(r.email.toLowerCase()))
        );
      } catch {
        setContactSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [contactSearchQuery, siteContacts]);

  // --- Mutations ---

  const invalidateContactQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['site-detail', siteId] });
    queryClient.invalidateQueries({ queryKey: ['site-contacts', siteId] });
  };

  // JIM-1018 (Jim 2026-10-18): adding an existing person made them the On-Site
  // Contact every time, demoting whoever Jim had chosen. A person added to a slot
  // that already has a primary joins as a plain contact; the star on a row (or the
  // box on the create form) is how the On-Site Contact is chosen. The consultant
  // slot keeps its one-of-each rule.
  const slotHasPrimary = (role: string) =>
    (siteContacts || []).some(
      (c) => c.is_primary && primaryGroup(c.role) === primaryGroup(role)
    );
  const primaryForAdd = (role: string) =>
    editContactModal.type === 'consultant' || !slotHasPrimary(role);

  // Mutation for adding a contact
  const addContactMutation = useMutation({
    mutationFn: async (data: {
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      company?: string;
      role: 'owner' | 'manager' | 'contractor' | 'inspector' | 'consultant';
      is_primary?: boolean;
      notify_email?: boolean;
    }) => {
      const added = await sitesService.addContact(siteId, data);
      const addedId = added?.contact?.id;
      // Jim 10/28 (Brookside 2 carried Jennifer, Jenna and Jarod): a site
      // has one consultant. Picking one in the Consultant slot replaces the
      // previous staff consultant(s) on the list instead of demoting them —
      // demoted rows stayed on every screen as a second and third consultant.
      if (editContactModal.type === 'consultant') {
        const previous = (siteContacts || []).filter(
          (c) =>
            c.is_active &&
            c.id !== addedId &&
            isConsultantSlot(c) &&
            (c.email || '').toLowerCase() !== data.email.toLowerCase()
        );
        await Promise.all(
          previous.map((c) => sitesService.deleteContact(siteId, c.id, false))
        );
        return added;
      }
      // A person added as the primary replaces the slot's current primary —
      // the consultant of record and the on-site contact are one each.
      if (data.is_primary) {
        const demote = (siteContacts || []).filter(
          (c) =>
            c.is_primary &&
            c.id !== addedId &&
            primaryGroup(c.role) === primaryGroup(data.role)
        );
        await Promise.all(
          demote.map((c) =>
            sitesService.updateContact(siteId, c.id, { is_primary: false })
          )
        );
      }
      return added;
    },
    onSuccess: () => {
      invalidateContactQueries();
      onClose();
      setContactMode('view');
      setNewContactForm({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: '',
        role: 'owner',
        is_primary: true,
        notify_email: true,
      });
      setContactSearchQuery('');
      setContactSearchResults([]);
      toast({
        title: 'Contact Added',
        description: 'Contact has been added successfully.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Contact not added',
        description: contactRefusalMessage(error),
        variant: 'destructive',
      });
    },
  });

  // Mutation for removing a contact from this site. NOTE: this is a per-row
  // action now, so it must NOT call onClose() — the modal stays open so the
  // user can keep managing the list.
  const deleteContactMutation = useMutation({
    mutationFn: (contactId: string) =>
      sitesService.deleteContact(siteId, contactId, false),
    onSuccess: () => {
      invalidateContactQueries();
      toast({
        title: 'Contact Removed',
        description: 'Contact removed from this site.',
      });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to remove contact. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Mutation for updating contact (set as primary)
  const updateContactMutation = useMutation({
    mutationFn: ({
      contactId,
      data,
    }: {
      contactId: string;
      data: {
        is_primary?: boolean;
        is_active?: boolean;
        notify_email?: boolean;
        can_sign_reports?: boolean;
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string | null;
        company?: string | null;
        title?: string | null;
      };
    }) => sitesService.updateContact(siteId, contactId, data),
    onSuccess: () => {
      invalidateContactQueries();
      setEditForm(null);
      setContactMode('view');
      toast({
        title: 'Contact Updated',
        description: 'Contact has been updated successfully.',
      });
    },
    onError: (e: unknown) => {
      // api.ts hands a FastAPI detail back as Error(detail): "another
      // person already uses that email", "staff are edited in People Hub".
      toast({
        title: 'Error',
        description:
          (e as { message?: string })?.message ||
          'Failed to update contact. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Set a contact as primary — promotes the chosen and demotes any existing
  // primary so there's exactly one. Primary = the first-call point of contact,
  // independent of whether they receive report emails.
  const setPrimaryMutation = useMutation({
    mutationFn: async (contactId: string) => {
      const chosen = (siteContacts || []).find((c) => c.id === contactId);
      // Demote only within the SAME role — a site contact's primary and the
      // consultant's primary share the is_primary column but are separate
      // per-role "primaries", so promoting one must not clobber the other.
      const demote = (siteContacts || []).filter(
        (c) =>
          c.is_primary &&
          c.id !== contactId &&
          primaryGroup(c.role) === primaryGroup(chosen?.role)
      );
      // Promote first, then demote: a failed demote leaves at most two primaries
      // (recoverable) rather than zero.
      await sitesService.updateContact(siteId, contactId, { is_primary: true });
      await Promise.all(
        demote.map((c) =>
          sitesService.updateContact(siteId, c.id, { is_primary: false })
        )
      );
    },
    onSuccess: () => {
      invalidateContactQueries();
      setContactMode('view');
      toast({ title: 'Primary contact updated' });
    },
    onError: () =>
      toast({
        title: 'Error',
        description: 'Failed to update primary contact.',
        variant: 'destructive',
      }),
  });

  // Confirm before removing — it's a hard removal of the site link (re-addable,
  // but not an undo), so guard against an accidental tap.
  const handleRemoveContact = async (contact: {
    id: string;
    first_name: string;
    last_name: string;
  }) => {
    const ok = await confirm({
      title: 'Remove contact?',
      description: `Remove ${contact.first_name} ${contact.last_name} from this site? You can re-add them later.`,
      confirmText: 'Remove',
      variant: 'destructive',
    });
    if (ok) deleteContactMutation.mutate(contact.id);
  };

  // The role-set that "primary" applies to for this modal — site contacts are
  // managers; consultants are contractors. Used to scope the Switch-Primary
  // action + its picker so it never lists/affects the other role.
  // JIM-1007-05: the Consultant slot takes EnviroServe staff only; a
  // client-side contractor is a site contact like a manager or owner.
  const isPeer = (c: SiteContact) =>
    editContactModal.type === 'consultant'
      ? isConsultantSlot(c)
      : isSiteContact(c);

  // --- Render ---

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
            {editContactModal.type === 'consultant' && (
              <>
                <User className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                {contactMode === 'select'
                  ? 'Select Consultant'
                  : contactMode === 'search_existing'
                    ? 'Find Existing Contact'
                    : editContactModal.contact
                      ? 'Consultant'
                      : 'Assign Consultant'}
              </>
            )}
            {editContactModal.type === 'site_contact' && (
              <>
                <UserCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                {contactMode === 'create'
                  ? 'Add Site Contact'
                  : contactMode === 'select'
                    ? 'Select Contact'
                    : contactMode === 'search_existing'
                      ? 'Find Existing Contact'
                      : contactMode === 'search_users'
                        ? 'Add from People Hub'
                        : editContactModal.contact
                          ? 'Site Contact'
                          : 'Add Site Contact'}
              </>
            )}
          </h3>
          <button
            onClick={() => {
              onClose();
              setContactMode('view');
            }}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* View Mode - Show current contact and actions */}
        {contactMode === 'view' && mailsEveryone && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
            Until this site is marked live, everyone on its contact list gets
            the report, switched on or not.
          </p>
        )}
        {contactMode === 'view' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {editContactModal.type === 'consultant' &&
                'The consultant is assigned to manage this site on behalf of EnviroLink.'}
              {editContactModal.type === 'site_contact' &&
                'Site contacts receive reports and communications for this site. The on-site contact (★) is the first point of contact — they don’t have to be on email.'}
            </p>

            {/* Actions kept ABOVE the list so adding/managing is up top, and the
                list grows down the modal (no inner scroll box) — Jim, 2026-06. */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Actions
              </p>
              <div className="flex flex-col gap-2">
                {siteContacts &&
                  siteContacts.filter((c) => c.is_active && isPeer(c)).length >
                    1 && (
                    <button
                      onClick={() => setContactMode('select')}
                      className="w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-left flex items-center gap-3"
                    >
                      <User className="w-4 h-4" />
                      {editContactModal.type === 'consultant'
                        ? 'Change Consultant'
                        : 'Switch On-site Contact'}
                    </button>
                  )}
                {/* JIM-1007-05: a consultant is an EnviroServe person, found in
                    the People Hub; the contacts search holds client people. */}
                {editContactModal.type !== 'consultant' && (
                  <button
                    onClick={() => {
                      setPickQuery('');
                      setUserSearchQuery('');
                      setContactMode('pick');
                    }}
                    className="w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-left flex items-center gap-3"
                  >
                    <Search className="w-4 h-4" />
                    Select Existing Contact
                  </button>
                )}
                {editContactModal.type === 'consultant' && (
                  <>
                    <button
                      onClick={() => {
                        setUserSearchQuery('');
                        setContactMode('search_users');
                      }}
                      className="w-full px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-left flex items-center gap-3"
                    >
                      <UserCheck className="w-4 h-4" />
                      Add from People Hub
                    </button>
                    <button
                      onClick={() => {
                        if (editContactModal.type === 'site_contact') {
                          setNewContactForm((prev) => ({
                            ...prev,
                            role: 'manager',
                          }));
                        }
                        setContactMode('create');
                      }}
                      className="w-full px-4 py-2.5 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors text-left flex items-center gap-3"
                    >
                      <Plus className="w-4 h-4" />
                      Create New Contact
                    </button>
                  </>
                )}
                {/* Single-contact remove kept only for the consultant card; site
                    contacts use the per-row remove in the list below. */}
                {editContactModal.type === 'consultant' &&
                  editContactModal.contact?.id && (
                    <button
                      onClick={() => {
                        if (editContactModal.contact?.id) {
                          // Single consultant card renders from a prop snapshot,
                          // so close the modal on success (unlike the per-row
                          // site-contact delete which keeps it open).
                          deleteContactMutation.mutate(
                            editContactModal.contact.id,
                            { onSuccess: () => onClose() }
                          );
                        }
                      }}
                      disabled={deleteContactMutation.isPending}
                      className="w-full px-4 py-2.5 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-left flex items-center gap-3 disabled:opacity-50"
                    >
                      {deleteContactMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                      Remove Consultant
                    </button>
                  )}
              </div>
            </div>

            {/* Jim 2026-10-15 (Magnolia Ridge): the consultant is not a site contact — this list
                follows the card's rule, so a consultant never shows here as the on-site contact */}
            {editContactModal.type === 'site_contact' &&
            siteContacts &&
            siteContacts.filter((c) => c.is_active && isPeer(c)).length > 0 ? (
              <div className="space-y-2">
                {siteContacts
                  .filter((c) => c.is_active && isPeer(c))
                  .map((contact) => (
                    <div
                      key={contact.id}
                      className={`bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 flex items-center justify-between gap-3 ${
                        contact.is_primary
                          ? 'ring-1 ring-primary-400 dark:ring-primary-600'
                          : ''
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-slate-900 dark:text-white flex items-center gap-1.5">
                          <span className="truncate">
                            {contact.first_name} {contact.last_name}
                          </span>
                          {contact.is_primary && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 shrink-0">
                              <Star className="w-2.5 h-2.5 fill-current" />
                              On-site Contact
                            </span>
                          )}
                        </p>
                        {contact.company && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                            {contact.company}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {contact.email}
                        </p>
                        {contact.phone && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {formatPhone(contact.phone)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {/* Make primary — only when not already primary */}
                        {!contact.is_primary && (
                          <button
                            onClick={() =>
                              setPrimaryMutation.mutate(contact.id)
                            }
                            disabled={setPrimaryMutation.isPending}
                            title="Make this person the On-Site Contact"
                            className="inline-flex items-center gap-1 px-1.5 py-1 rounded text-xs text-slate-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
                          >
                            <Star className="w-4 h-4" />
                            <span className="hidden sm:inline">
                              {primaryBadge(contact.role)}
                            </span>
                          </button>
                        )}
                        {/* Email on/off — real toggle switch. Each switch
                            carries a one-word caption: two icon-only
                            switches side by side did not say which was
                            which. */}
                        <div
                          className="flex flex-col items-center gap-1"
                          title={
                            contact.notify_email
                              ? 'Receives report emails — toggle to mute'
                              : 'Muted — toggle to enable report emails'
                          }
                        >
                          <Switch
                            aria-label={`Report emails: ${contact.first_name} ${contact.last_name}`}
                            checked={!!contact.notify_email}
                            disabled={updateContactMutation.isPending}
                            onCheckedChange={() =>
                              updateContactMutation.mutate({
                                contactId: contact.id,
                                data: { notify_email: !contact.notify_email },
                              })
                            }
                          />
                          <span className="flex items-center gap-1 text-[10px] leading-none text-slate-500 dark:text-slate-400">
                            {contact.notify_email ? (
                              <Mail className="w-3 h-3 text-green-600 dark:text-green-400" />
                            ) : (
                              <MailX className="w-3 h-3 text-slate-400" />
                            )}
                            Emails
                          </span>
                        </div>
                        {/* JIM-1003-03 (Jim 2026-10-03): "this is a contact
                            that's approved to sign the report". Only ticked
                            contacts get the signing links in the report
                            email; off for everyone until switched on. */}
                        <div
                          className="flex flex-col items-center gap-1"
                          title={
                            contact.can_sign_reports
                              ? 'Signs inspection reports for the client — toggle to stop'
                              : 'Does not sign reports — toggle to make them a signer'
                          }
                        >
                          <Switch
                            aria-label={`Signs reports: ${contact.first_name} ${contact.last_name}`}
                            checked={!!contact.can_sign_reports}
                            disabled={updateContactMutation.isPending}
                            onCheckedChange={() =>
                              updateContactMutation.mutate({
                                contactId: contact.id,
                                data: {
                                  can_sign_reports: !contact.can_sign_reports,
                                },
                              })
                            }
                          />
                          <span className="flex items-center gap-1 text-[10px] leading-none text-slate-500 dark:text-slate-400">
                            <PenLine
                              className={`w-3 h-3 ${
                                contact.can_sign_reports
                                  ? 'text-emerald-600 dark:text-emerald-400'
                                  : 'text-slate-400'
                              }`}
                            />
                            Signs
                          </span>
                        </div>
                        {/* Edit the person in place (Jim 10/21); staff
                            records belong to People Hub. */}
                        {!contact.is_staff && (
                          <button
                            onClick={() => {
                              setEditForm({
                                id: contact.id,
                                first_name: contact.first_name,
                                last_name: contact.last_name,
                                email: contact.email,
                                phone: contact.phone || '',
                                company: contact.company || '',
                                title: contact.title || '',
                              });
                              setContactMode('edit');
                            }}
                            title="Edit name, email or phone"
                            className="p-1 rounded text-slate-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        {/* Remove from site */}
                        <button
                          onClick={() => handleRemoveContact(contact)}
                          disabled={deleteContactMutation.isPending}
                          title="Remove from site"
                          className="p-1 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                {/* Jim 10/30, Tash 11/2: staff on the site's list (the consultant
                    slot, an inspector) were invisible here yet still on the send
                    list. Shown with the one switch that matters; their names
                    and emails stay People Hub's. */}
                {siteContacts.filter(
                  (c) => c.is_active && c.is_staff && !isPeer(c)
                ).length > 0 && (
                  <div
                    className="pt-2 mt-2 border-t border-slate-200 dark:border-slate-700"
                    data-testid="staff-on-site"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                      EnviroServe staff on this site
                    </p>
                    {siteContacts
                      .filter((c) => c.is_active && c.is_staff && !isPeer(c))
                      .map((contact) => (
                        <div
                          key={contact.id}
                          className="flex items-center justify-between py-1.5"
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-slate-800 dark:text-slate-200 truncate">
                              {contact.first_name} {contact.last_name}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                              {contact.role} · {contact.email}
                            </p>
                          </div>
                          <div
                            className="flex flex-col items-center gap-1"
                            title={
                              contact.notify_email
                                ? 'Receives report emails — toggle to mute'
                                : 'Muted — toggle to enable report emails'
                            }
                          >
                            <Switch
                              aria-label={`Report emails: ${contact.first_name} ${contact.last_name}`}
                              checked={!!contact.notify_email}
                              disabled={updateContactMutation.isPending}
                              onCheckedChange={() =>
                                updateContactMutation.mutate({
                                  contactId: contact.id,
                                  data: { notify_email: !contact.notify_email },
                                })
                              }
                            />
                            <span className="flex items-center gap-1 text-[10px] leading-none text-slate-500 dark:text-slate-400">
                              {contact.notify_email ? (
                                <Mail className="w-3 h-3 text-green-600 dark:text-green-400" />
                              ) : (
                                <MailX className="w-3 h-3 text-slate-400" />
                              )}
                              Emails
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : editContactModal.type !== 'site_contact' &&
              editContactModal.contact ? (
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                <p className="font-medium text-slate-900 dark:text-white">
                  {editContactModal.contact.first_name}{' '}
                  {editContactModal.contact.last_name}
                </p>
                {editContactModal.contact.company && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {editContactModal.contact.company}
                  </p>
                )}
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {editContactModal.contact.email}
                </p>
                {editContactModal.contact.phone && (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {formatPhone(editContactModal.contact.phone)}
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  No{' '}
                  {editContactModal.type === 'site_contact'
                    ? 'site contact'
                    : editContactModal.type}{' '}
                  is currently assigned to this site.
                </p>
              </div>
            )}

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  onClose();
                  setContactMode('view');
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Select Mode - Show list of existing contacts */}
        {contactMode === 'pick' && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={pickQuery}
                onChange={(e) => {
                  setPickQuery(e.target.value);
                  setUserSearchQuery(e.target.value);
                }}
                placeholder="Find a person by name, email or company..."
                aria-label="Find a contact"
                className="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {clientPeopleLoading ? (
                <p className="text-center py-4 text-sm text-slate-400">
                  Loading…
                </p>
              ) : pickList.length > 0 ||
                hubMatches.length > 0 ||
                alreadyHere.length > 0 ? (
                <>
                  {pickList.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        addContactMutation.mutate({
                          first_name: p.first_name || '',
                          last_name: p.last_name || '',
                          email: p.email || '',
                          company: p.company || undefined,
                          role: 'manager',
                          is_primary: primaryForAdd('manager'),
                        })
                      }
                      disabled={addContactMutation.isPending || !p.email}
                      className="w-full p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left transition-colors disabled:opacity-50"
                    >
                      <p className="font-medium text-slate-900 dark:text-white">
                        {p.first_name} {p.last_name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {p.email}
                        {p.company ? ` · ${p.company}` : ''}
                      </p>
                    </button>
                  ))}
                  {alreadyHere.map((u) => (
                    <div
                      key={`here-${u.id}`}
                      data-testid="search-already-on-site"
                      className="w-full p-3 rounded-lg border border-dashed border-slate-200 dark:border-slate-700 text-left opacity-80"
                    >
                      <p className="font-medium text-slate-900 dark:text-white">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {u.email} · already on this site
                      </p>
                    </div>
                  ))}
                  {hubMatches.map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      className="w-full text-left p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-slate-700/50 dark:hover:border-primary-500"
                      onClick={() =>
                        addContactMutation.mutate({
                          first_name: u.firstName,
                          last_name: u.lastName,
                          email: u.email,
                          role: 'manager',
                          is_primary: primaryForAdd('manager'),
                        })
                      }
                      disabled={addContactMutation.isPending}
                    >
                      {/* JIM-1027-16: these rows were the one unthemed block
                          and read dark-on-dark on the phone. */}
                      <p className="font-medium text-slate-900 dark:text-white">
                        {u.firstName} {u.lastName}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {u.email} · on file
                      </p>
                    </button>
                  ))}
                </>
              ) : (
                <p className="text-center py-4 text-sm text-slate-400 dark:text-slate-500">
                  {pickQuery
                    ? 'Nobody matches. Create a new contact below.'
                    : "Nobody else is on this client's sites yet."}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              <button
                type="button"
                onClick={() => setContactMode('view')}
                className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:underline"
              >
                Current contacts (
                {
                  (siteContacts || []).filter((c) => c.is_active && isPeer(c))
                    .length
                }
                )
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setNewContactForm((prev) => ({ ...prev, role: 'manager' }));
                    setContactMode('create');
                  }}
                  className="px-3 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  New contact
                </button>
              </div>
            </div>
          </div>
        )}

        {contactMode === 'select' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Select a contact from the list below to assign to this site.
            </p>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {siteContacts &&
              siteContacts.filter((c) => c.is_active && isPeer(c)).length >
                0 ? (
                siteContacts
                  .filter((c) => c.is_active && isPeer(c))
                  .map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => setPrimaryMutation.mutate(contact.id)}
                      disabled={setPrimaryMutation.isPending}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        editContactModal.contact?.id === contact.id
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {contact.first_name} {contact.last_name}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {contact.email}
                          </p>
                          {contact.company && (
                            <p className="text-xs text-slate-400 dark:text-slate-500">
                              {contact.company}
                            </p>
                          )}
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 capitalize">
                          {contact.role}
                        </span>
                      </div>
                    </button>
                  ))
              ) : (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No contacts found for this site.</p>
                  <button
                    onClick={() => setContactMode('create')}
                    className="mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Create a new contact
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setContactMode('view')}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setContactMode('create')}
                className="px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create New
              </button>
            </div>
          </div>
        )}

        {/* Search Existing Mode - Find contact from any site */}
        {contactMode === 'search_existing' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Search for an existing contact by name, email, or company to add
              them to this site.
            </p>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={contactSearchQuery}
                onChange={(e) => setContactSearchQuery(e.target.value)}
                placeholder="Search by name, email, or company..."
                className="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {contactSearchResults.length > 0 ? (
                contactSearchResults.map((contact) => (
                  <button
                    key={contact.id}
                    onClick={() => {
                      // Copy contact data to create on this site
                      // JIM-1007-03 (Jim 2026-10-07): a consultant added through
                      // the Consultant slot is a consultant, not a contractor,
                      // so the list and the reports call them that.
                      const roleForType =
                        editContactModal.type === 'consultant'
                          ? 'consultant'
                          : editContactModal.type === 'site_contact'
                            ? 'manager'
                            : contact.role;
                      addContactMutation.mutate({
                        first_name: contact.first_name,
                        last_name: contact.last_name,
                        email: contact.email,
                        phone: contact.phone || undefined,
                        company: contact.company || undefined,
                        role: roleForType as
                          | 'owner'
                          | 'manager'
                          | 'contractor'
                          | 'inspector'
                          | 'consultant',
                        is_primary: primaryForAdd(roleForType),
                      });
                    }}
                    disabled={addContactMutation.isPending}
                    className="w-full p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left transition-colors disabled:opacity-50"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {contact.first_name} {contact.last_name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {contact.email}
                        </p>
                        {contact.company && (
                          <p className="text-xs text-slate-400 dark:text-slate-500">
                            {contact.company}
                          </p>
                        )}
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 capitalize">
                        {contact.role}
                      </span>
                    </div>
                  </button>
                ))
              ) : contactSearchQuery.length >= 2 ? (
                <div className="text-center py-6 text-slate-500 dark:text-slate-400">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No matching contacts found.</p>
                  <button
                    onClick={() => {
                      // Pre-fill email if it looks like an email was searched
                      const isEmail = contactSearchQuery.includes('@');
                      setNewContactForm((prev) => ({
                        ...prev,
                        email: isEmail ? contactSearchQuery : '',
                        role:
                          editContactModal.type === 'consultant'
                            ? 'consultant'
                            : editContactModal.type === 'site_contact'
                              ? 'manager'
                              : prev.role,
                      }));
                      setContactMode('create');
                    }}
                    className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Create a new contact instead
                  </button>
                </div>
              ) : (
                <p className="text-center py-4 text-sm text-slate-400 dark:text-slate-500">
                  Type at least 2 characters to search
                </p>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setContactMode('view')}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => {
                  setNewContactForm((prev) => ({
                    ...prev,
                    role:
                      editContactModal.type === 'consultant'
                        ? 'consultant'
                        : editContactModal.type === 'site_contact'
                          ? 'manager'
                          : prev.role,
                  }));
                  setContactMode('create');
                }}
                className="px-4 py-2 text-sm font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create New
              </button>
            </div>
          </div>
        )}

        {/* People Hub Mode - Search EnviroLink users */}
        {contactMode === 'search_users' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {consultantSlot
                ? 'EnviroServe consultants, and inspectors who also consult. Pick one for this site.'
                : 'Search EnviroLink employees and contractors to add as a site contact. They will receive emailed reports.'}
            </p>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {staffSearchResults.length > 0 ? (
                staffSearchResults.map((u: any) => {
                  // Site contacts are report-distribution contacts and the
                  // site-contacts list only renders role 'manager', so always
                  // add as manager regardless of the user's system role —
                  // an inspector-role user otherwise vanishes from the list
                  // (Jim, 2026-06).
                  const roleForType =
                    editContactModal.type === 'consultant'
                      ? 'consultant'
                      : 'manager';
                  return (
                    <button
                      key={u.id}
                      onClick={() => {
                        addContactMutation.mutate({
                          first_name: u.firstName,
                          last_name: u.lastName,
                          email: u.email,
                          phone: u.phone || undefined,
                          role: roleForType as
                            | 'owner'
                            | 'manager'
                            | 'contractor'
                            | 'inspector',
                          is_primary: primaryForAdd(roleForType),
                        });
                      }}
                      disabled={
                        addContactMutation.isPending || !!u.currentConsultant
                      }
                      data-testid={
                        u.currentConsultant ? 'current-consultant' : undefined
                      }
                      className="w-full p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-left transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">
                            {u.firstName} {u.lastName}
                            {u.currentConsultant && (
                              <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                                this site's consultant
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {u.email}
                          </p>
                        </div>
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 capitalize">
                          {u.role}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : userSearchQuery.length >= 2 || consultantSlot ? (
                <div className="text-center py-6 text-slate-500 dark:text-slate-400">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {consultantSlot
                      ? 'No consultants match.'
                      : 'No users found.'}
                  </p>
                </div>
              ) : (
                <p className="text-center py-4 text-sm text-slate-400 dark:text-slate-500">
                  Type at least 2 characters to search
                </p>
              )}
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setContactMode('view')}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Create Mode - Form to add new contact */}
        {contactMode === 'edit' && editForm && (
          <div className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Changes apply everywhere this person is listed; report emails go
              to the new address from the next send.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, first_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, last_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, email: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm({
                      ...editForm,
                      phone: formatPhoneInput(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Title (on this site)
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) =>
                    setEditForm({ ...editForm, title: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Company
              </label>
              <input
                type="text"
                value={editForm.company}
                onChange={(e) =>
                  setEditForm({ ...editForm, company: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div className="flex justify-between mt-6">
              <button
                onClick={() => {
                  setEditForm(null);
                  setContactMode('view');
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  updateContactMutation.mutate({
                    contactId: editForm.id,
                    data: {
                      first_name: editForm.first_name.trim(),
                      last_name: editForm.last_name.trim(),
                      email: editForm.email.trim(),
                      phone: editForm.phone.trim() || null,
                      company: editForm.company.trim() || null,
                      title: editForm.title.trim() || null,
                    },
                  })
                }
                disabled={
                  updateContactMutation.isPending ||
                  !editForm.first_name.trim() ||
                  !editForm.last_name.trim() ||
                  !editForm.email.trim()
                }
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updateContactMutation.isPending && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                Save changes
              </button>
            </div>
          </div>
        )}

        {contactMode === 'create' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={newContactForm.first_name}
                  onChange={(e) =>
                    setNewContactForm({
                      ...newContactForm,
                      first_name: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={newContactForm.last_name}
                  onChange={(e) =>
                    setNewContactForm({
                      ...newContactForm,
                      last_name: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Doe"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Email *
              </label>
              <input
                type="email"
                value={newContactForm.email}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    email: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="john.doe@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={newContactForm.phone}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    phone: formatPhoneInput(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Company
              </label>
              <input
                type="text"
                value={newContactForm.company}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    company: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Acme Construction"
              />
            </div>

            {/* Role is implied by context for consultants (contractor) and
                site contacts (manager); only offer the picker elsewhere so a
                new contact can't be created with a role its section hides. */}
            {editContactModal.type !== 'consultant' &&
              editContactModal.type !== 'site_contact' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Role *
                  </label>
                  <select
                    value={newContactForm.role}
                    onChange={(e) =>
                      setNewContactForm({
                        ...newContactForm,
                        role: e.target.value as
                          | 'owner'
                          | 'manager'
                          | 'contractor'
                          | 'inspector'
                          | 'consultant',
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="contractor">Contractor</option>
                    <option value="inspector">Inspector</option>
                  </select>
                </div>
              )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_primary"
                checked={newContactForm.is_primary}
                onChange={(e) =>
                  setNewContactForm({
                    ...newContactForm,
                    is_primary: e.target.checked,
                  })
                }
                className="w-4 h-4 text-primary-600 border-slate-300 dark:border-slate-600 rounded focus:ring-primary-500"
              />
              <label
                htmlFor="is_primary"
                className="text-sm text-slate-700 dark:text-slate-300"
              >
                Set as primary contact
              </label>
            </div>

            {/* Email on/off right here on the add screen (Jim, 2026-06) */}
            <div className="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-700/40 rounded-lg px-3 py-2">
              <label
                htmlFor="notify_email"
                className="text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2 cursor-pointer"
              >
                {newContactForm.notify_email ? (
                  <Mail className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <MailX className="w-4 h-4 text-slate-400" />
                )}
                Receive inspection report emails
              </label>
              <Switch
                id="notify_email"
                checked={newContactForm.notify_email}
                onCheckedChange={(v) =>
                  setNewContactForm({ ...newContactForm, notify_email: v })
                }
              />
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={() => setContactMode('view')}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              {(() => {
                const emailLower = newContactForm.email.trim().toLowerCase();
                const duplicate =
                  emailLower &&
                  (siteContacts || []).find(
                    (c) => c.email.toLowerCase() === emailLower
                  );
                return duplicate ? (
                  <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                    A contact with this email already exists on this site (
                    {duplicate.first_name} {duplicate.last_name}).
                  </p>
                ) : null;
              })()}

              <button
                onClick={() => {
                  const emailLower = newContactForm.email.trim().toLowerCase();
                  const duplicate =
                    emailLower &&
                    (siteContacts || []).find(
                      (c) => c.email.toLowerCase() === emailLower
                    );
                  if (duplicate) {
                    toast({
                      title: 'Duplicate Email',
                      description: `${duplicate.first_name} ${duplicate.last_name} already has this email on this site.`,
                      variant: 'destructive',
                    });
                    return;
                  }
                  if (
                    newContactForm.first_name &&
                    newContactForm.last_name &&
                    newContactForm.email
                  ) {
                    addContactMutation.mutate({
                      first_name: newContactForm.first_name,
                      last_name: newContactForm.last_name,
                      email: newContactForm.email,
                      phone: newContactForm.phone || undefined,
                      company: newContactForm.company || undefined,
                      // Force the contextual role so the new contact actually
                      // renders in its section (site-contacts list filters role
                      // 'manager'; consultants filter 'contractor') — otherwise
                      // an owner/inspector default is created but never shown.
                      role:
                        editContactModal.type === 'consultant'
                          ? 'consultant'
                          : editContactModal.type === 'site_contact'
                            ? 'manager'
                            : newContactForm.role,
                      is_primary: newContactForm.is_primary,
                      notify_email: newContactForm.notify_email,
                    });
                  } else {
                    toast({
                      title: 'Missing Required Fields',
                      description:
                        'Please fill in first name, last name, and email.',
                      variant: 'destructive',
                    });
                  }
                }}
                disabled={
                  addContactMutation.isPending ||
                  !!(
                    newContactForm.email.trim() &&
                    (siteContacts || []).find(
                      (c) =>
                        c.email.toLowerCase() ===
                        newContactForm.email.trim().toLowerCase()
                    )
                  )
                }
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {addContactMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Add Contact
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
